import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertCircle,
  Briefcase,
  User,
  Building2,
  Mail,
} from "lucide-react";
import { ProjectService } from "../services/projectService";
import { DiscrepancyService } from "../services/discrepancyService";
import { supabase } from "../lib/supabase";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { buildStructuredEmail } from "../utils/emailHtmlFormatter";
import { Modal, ModalBody, ModalFooter, Button } from "./ui";
import { formatJobRevNumber } from '../utils/projectFormatting';
import "./HoldProjectModal.css";
import "./ExportAndSend/ExportForm.css";

const HOLD_REASONS = [
  { value: "waiting_updated_pdfs", label: "Waiting on Updated PDFs" },
  { value: "waiting_updated_cad", label: "Waiting on Updated CAD Files" },
  {
    value: "waiting_discrepancy_clarification",
    label: "Waiting on Discrepancy Clarification",
  },
  { value: "client_requested_hold", label: "Client Requested Hold" },
  { value: "other", label: "Other" },
];

const HoldProjectModal = ({ project, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    holdReason: "",
    holdNotes: "",
    holdInitialFollowUpDays: 7,
    holdRecurringFollowUpDays: 14,
    holdSendEmailEnabled: false,
    holdEmailPreview: "",
    holdEmailHtml: "",
    holdEmailTo: [],
    holdEmailCc: [],
    holdEmailSubject: "",
    holdDiscrepancyIds: [],
    holdCustomMessage: "",
  });
  const [clientContacts, setClientContacts] = useState([]);
  const [projectDiscrepancies, setProjectDiscrepancies] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingDiscrepancies, setLoadingDiscrepancies] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const htmlUpdateTimerRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    const loadData = async () => {
      if (!isOpen || !project) return;

      const contactId = project?.contactId || project?.contact_id;
      const defaultEmailTo = contactId ? [contactId] : [];

      setFormData({
        holdReason: "",
        holdNotes: "",
        holdInitialFollowUpDays: 7,
        holdRecurringFollowUpDays: 14,
        holdSendEmailEnabled: false,
        holdEmailPreview: "",
        holdEmailHtml: "",
        holdEmailTo: defaultEmailTo,
        holdEmailCc: [],
        holdEmailSubject: "",
        holdDiscrepancyIds: [],
        holdCustomMessage: "",
      });
      setError(null);
      setClientContacts([]);
      setProjectDiscrepancies([]);

      const clientId = project.clientId || project.client_id;
      if (clientId) {
        try {
          const contacts = await ProjectService.getContactsByClientId(clientId);
          setClientContacts(contacts || []);

          // Auto-fill "To" field with project contact if available and emailTo is empty
          const contactId = project?.contactId || project?.contact_id;
          if (contactId && defaultEmailTo.length === 0) {
            setFormData((prev) => ({
              ...prev,
              holdEmailTo: [contactId],
            }));
          }
        } catch (err) {
          console.error("Error loading contacts:", err);
          setClientContacts([]);
        }
      }
      await loadDiscrepancies(project.id);
      await loadCurrentUser();
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, project]);

  const handlePrepareEmail = useCallback(async () => {
    if (!formData.holdReason) {
      setError("Please select a hold reason first");
      return;
    }

    setSaving(true);
    try {
      const template = await ProjectService.getHoldReasonEmailTemplate(formData.holdReason);

      if (template) {
        // Auto-populate To field with project contact if empty
        let emailTo = formData.holdEmailTo;
        const contactId = project?.contactId || project?.contact_id;
        if (emailTo.length === 0 && contactId) {
          emailTo = [contactId];
        }

        // Get contact first name for personalized greeting
        const primaryContact = clientContacts.find(c =>
          emailTo.length > 0 && c.id === emailTo[0]
        );
        const contactName = primaryContact?.name || "";
        const contactFirstName = contactName.split(" ")[0] || contactName;

        // Get project name for personalization
        const projectName = project?.projectName || project?.name || "This project";

        // Build template message - replace "This project" with actual project name
        const templateMessage = template
          .replace('This project has been put on hold', `${projectName} is being placed on hold`)
          .replace('This project is being put on hold', `${projectName} is being placed on hold`)
          .replace(/\.$/, ''); // Remove trailing period

        // Build hold notice messages
        const holdNoticeMessages = [
          `${templateMessage}.`,
          `We will send a reminder email in ${formData.holdInitialFollowUpDays} days.`
        ];

        // Add hold notes if provided
        if (formData.holdNotes) {
          holdNoticeMessages.push(`Additional Notes: ${formData.holdNotes}`);
        }

        // Build discrepancies array with structured data
        const structuredDiscrepancies = [];
        if (formData.holdDiscrepancyIds && formData.holdDiscrepancyIds.length > 0) {
          const selectedDiscrepancies = projectDiscrepancies.filter((d) =>
            formData.holdDiscrepancyIds.includes(d.id),
          );

          selectedDiscrepancies.forEach((discrepancy) => {
            const hasLoomLink = discrepancy.loomLink && discrepancy.loomLink.trim();
            const filesWithUrls = (discrepancy.markupAttachment || []).filter(file => {
              const fileUrl = file.url || file.file_url || file.publicUrl || file.public_url || file.direct_download_url || file.signed_url || '';
              return fileUrl && fileUrl.trim() && fileUrl.startsWith('http');
            });

            // Only add discrepancy if it has video or files
            if (hasLoomLink || filesWithUrls.length > 0) {
              structuredDiscrepancies.push({
                type: discrepancy.discrepancyType || 'Discrepancy',
                videoUrl: hasLoomLink ? discrepancy.loomLink : '',
                fileLinks: filesWithUrls.map(file => ({
                  url: file.url || file.file_url || file.publicUrl || file.public_url || file.direct_download_url || file.signed_url,
                  filename: file.filename || file.original_filename || file.name || 'Markup File'
                }))
              });
            }
          });
        }

        // Generate subject line
        const holdReasonLabel = HOLD_REASONS.find((r) => r.value === formData.holdReason)?.label || "Status Update";
        const emailSubject = `${projectName} - On Hold: ${holdReasonLabel}`;

        // Generate email HTML using buildStructuredEmail
        const htmlContent = buildStructuredEmail({
          greeting: `${contactFirstName},`,
          templateMessages: [],
          discrepancies: structuredDiscrepancies,
          holdNoticeMessages: holdNoticeMessages,
          customMessage: formData.holdCustomMessage || "",
          signature: currentUser?.email_signature_html || "",
        });

        // Also build plain text preview for storage (backwards compatibility)
        let emailPreview = `${contactFirstName},\n\n`;
        emailPreview += `${templateMessage}. We will send a reminder email in ${formData.holdInitialFollowUpDays} days.\n\n`;
        if (formData.holdNotes) {
          emailPreview += `Additional Notes:\n${formData.holdNotes}\n\n`;
        }

        // Update form data with both preview and HTML
        setFormData((prev) => ({
          ...prev,
          holdEmailTo: emailTo,
          holdEmailPreview: emailPreview,
          holdEmailSubject: emailSubject,
          holdEmailHtml: htmlContent,
        }));
      }
    } catch (err) {
      console.error("Error preparing email:", err);
      setError("Failed to load email template");
    } finally {
      setSaving(false);
    }
  }, [formData.holdReason, formData.holdEmailTo, formData.holdInitialFollowUpDays, formData.holdNotes, formData.holdDiscrepancyIds, formData.holdCustomMessage, project, clientContacts, projectDiscrepancies, currentUser]);

  // Auto-generate email when email is enabled and hold reason is set
  useEffect(() => {
    if (formData.holdSendEmailEnabled && formData.holdReason && clientContacts.length > 0) {
      // Use setTimeout to ensure this runs after state updates
      const timer = setTimeout(() => {
        handlePrepareEmail();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [formData.holdSendEmailEnabled, formData.holdReason, clientContacts.length, handlePrepareEmail]);

  const updateEmailHTML = useCallback(() => {
    if (!formData.holdEmailPreview) return;

    try {
      // Get contact first name for personalized greeting
      const primaryContact = clientContacts.find(contact =>
        formData.holdEmailTo.length > 0 && contact.id === formData.holdEmailTo[0]
      );
      const contactName = primaryContact?.name || '';
      const contactFirstName = contactName.split(' ')[0] || contactName;

      // Parse the hold email preview to extract template messages and hold notice
      const previewLines = formData.holdEmailPreview.split('\n').filter(line => line.trim());

      // Extract greeting (first line with just name)
      const greeting = previewLines.length > 0 && previewLines[0].includes(',')
        ? previewLines[0]
        : `${contactFirstName},`;

      // Build template messages (everything before hold notice)
      const templateMessages = [];
      const holdNoticeMessages = [];
      let foundHoldNotice = false;

      for (const line of previewLines) {
        if (line.includes('placed on hold') || line.includes('being placed on hold')) {
          foundHoldNotice = true;
          holdNoticeMessages.push(line);
        } else if (foundHoldNotice) {
          holdNoticeMessages.push(line);
        } else if (!line.includes(',') || line.split(' ').length > 2) {
          // Skip greeting line
          templateMessages.push(line);
        }
      }

      // Generate HTML using buildStructuredEmail
      const htmlContent = buildStructuredEmail({
        greeting: greeting,
        templateMessages: templateMessages,
        discrepancies: [],
        holdNoticeMessages: holdNoticeMessages,
        customMessage: formData.holdCustomMessage || "",
        signature: currentUser?.email_signature_html || "",
      });

      setFormData(prev => ({
        ...prev,
        holdEmailHtml: htmlContent,
      }));
    } catch (error) {
      console.error('Error formatting hold email HTML:', error);
    }
  }, [formData.holdEmailPreview, formData.holdCustomMessage, formData.holdEmailTo, currentUser, clientContacts]);

  // Auto-regenerate email HTML when custom message changes (debounced)
  useEffect(() => {
    if (!formData.holdSendEmailEnabled || !formData.holdEmailPreview) return;

    if (htmlUpdateTimerRef.current) {
      clearTimeout(htmlUpdateTimerRef.current);
    }

    htmlUpdateTimerRef.current = setTimeout(() => {
      updateEmailHTML();
    }, 1000);

    return () => {
      if (htmlUpdateTimerRef.current) {
        clearTimeout(htmlUpdateTimerRef.current);
      }
    };
  }, [formData.holdCustomMessage, formData.holdEmailPreview, formData.holdSendEmailEnabled, updateEmailHTML]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);

    if (field === "holdSendEmailEnabled" && value === true) {
      const clientId = project?.clientId || project?.client_id;
      if (clientId && clientContacts.length === 0) {
        loadContacts(clientId);
      }
      // Email generation is now handled by useEffect
    }
  };

  const loadContacts = async (clientId) => {
    if (!clientId) {
      clientId = project?.client_id || project?.clientId;
      if (!clientId) return;
    }

    setLoadingContacts(true);
    try {
      const contacts = await ProjectService.getContactsByClientId(clientId);
      setClientContacts(contacts || []);

      // Auto-fill "To" field with project contact if available and emailTo is empty
      const contactId = project?.contactId || project?.contact_id;
      if (contactId && formData.holdEmailTo.length === 0) {
        setFormData((prev) => ({
          ...prev,
          holdEmailTo: [contactId],
        }));
      }
    } catch (err) {
      console.error("Error loading contacts:", err);
      setClientContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadDiscrepancies = async (projectId) => {
    if (!projectId) return;

    setLoadingDiscrepancies(true);
    try {
      const discrepancies = await DiscrepancyService.getDiscrepanciesByProjectId(projectId);
      setProjectDiscrepancies(discrepancies || []);
    } catch (err) {
      console.error("Error loading discrepancies:", err);
      setProjectDiscrepancies([]);
    } finally {
      setLoadingDiscrepancies(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { data: userData, error: profileError } = await supabase
          .from('employees')
          .select('*')
          .eq('email', user.email)
          .single();

        if (!profileError && userData) {
          setCurrentUser(userData);
        }
      }
    } catch (err) {
      console.error("Error loading current user:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.holdReason) {
      setError("Please select a hold reason");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const holdData = {
        modeling_state: "Hold",
        hold_reason: formData.holdReason,
        hold_notes: formData.holdNotes,
        hold_initial_follow_up_days: formData.holdInitialFollowUpDays,
        hold_recurring_follow_up_days: formData.holdRecurringFollowUpDays,
        hold_discrepancy_ids: formData.holdDiscrepancyIds || [],
        hold_custom_message: formData.holdCustomMessage || null,
      };

      const { error: updateError } = await supabase
        .from('projects')
        .update(holdData)
        .eq('id', project.id)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to update project: ${updateError.message}`);
      }

      if (formData.holdSendEmailEnabled && formData.holdEmailPreview && formData.holdEmailTo.length > 0) {
        const attachmentUrls = [];
        let discrepancyData = null;

        if (formData.holdDiscrepancyIds && formData.holdDiscrepancyIds.length > 0) {
          const selectedDiscrepancies = projectDiscrepancies.filter(d =>
            formData.holdDiscrepancyIds.includes(d.id)
          );

          const discrepancyDetails = [];

          selectedDiscrepancies.forEach(discrepancy => {
            if (discrepancy.markupAttachment && discrepancy.markupAttachment.length > 0) {
              discrepancy.markupAttachment.forEach(file => {
                if (file.url) {
                  attachmentUrls.push(file.url);
                }
              });
            }

            discrepancyDetails.push({
              id: discrepancy.id,
              type: discrepancy.discrepancyType || 'Discrepancy',
              additional_information: discrepancy.additionalInformation || '',
              attachments: discrepancy.markupAttachment || [],
              loom_link: discrepancy.loomLink || null,
              created_date: discrepancy.createdDate || discrepancy.created_date || null
            });
          });

          discrepancyData = {
            discrepancy_count: selectedDiscrepancies.length,
            discrepancy_ids: formData.holdDiscrepancyIds,
            discrepancy_details: discrepancyDetails
          };
        }

        await ProjectService.sendHoldEmail(
          project.id,
          formData.holdEmailPreview + (formData.holdCustomMessage ? `\n\n${formData.holdCustomMessage}` : ''),
          formData.holdEmailTo,
          formData.holdEmailCc,
          formData.holdEmailSubject,
          attachmentUrls,
          discrepancyData
        );
      }

      setTimeout(async () => {
        await onSuccess();
        onClose();
      }, 500);
    } catch (err) {
      console.error("Error putting project on hold:", err);
      setError(err.message || "Failed to put project on hold");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal trackAs="HoldProjectModal"
      isOpen={isOpen}
      onClose={onClose}
      size={formData.holdSendEmailEnabled ? 'xl' : 'lg'}
    >
      {/* Custom Header - Match ProjectDetailsModal */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-400)',
              flexShrink: 0,
            }}
          >
            {formatJobRevNumber(project?.jobNumber || project?.job_number, project?.rev)}
          </span>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-50)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {project?.projectName || project?.name}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <ModalBody>
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--danger)',
              fontSize: 'var(--text-sm)',
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Client & Contact Section - Matching ProjectDetailsModal style */}
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h2 style={{
              margin: 0,
              marginBottom: 'var(--space-4)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
            }}>
              Client & Contact
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {/* Client */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}>
                <Building2 size={16} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Client
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                    {project?.client?.name || project?.clientName || 'Not Specified'}
                  </div>
                </div>
              </div>

              {/* Contact */}
              {project?.contact && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--dark-surface)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-lg)',
                }}>
                  <User size={16} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-400)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Contact
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                      {project.contact.name || 'Not Specified'}
                    </div>
                  </div>
                </div>
              )}

              {/* Email */}
              {project?.contact?.email && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--dark-surface)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-lg)',
                }}>
                  <Mail size={14} style={{ color: 'var(--cta-blue)', marginTop: '2px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-400)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Email
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                      {project.contact.email}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Hold Details Section */}
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h2 style={{
              margin: 0,
              marginBottom: 'var(--space-4)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
            }}>
              Hold Details
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {/* Hold Reason */}
              <div>
                <label htmlFor="holdReason" style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                }}>
                  Hold Reason <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <select
                  id="holdReason"
                  value={formData.holdReason}
                  onChange={(e) => handleInputChange("holdReason", e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--gray-200)',
                    fontSize: 'var(--text-sm)',
                    cursor: 'pointer',
                    transition: 'border-color var(--transition-fast)',
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-700)'}
                >
                  <option value="">Select a reason...</option>
                  {HOLD_REASONS.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Hold Notes */}
              <div>
                <label htmlFor="holdNotes" style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                }}>
                  Additional Notes
                </label>
                <textarea
                  id="holdNotes"
                  value={formData.holdNotes}
                  onChange={(e) => handleInputChange("holdNotes", e.target.value)}
                  rows={3}
                  placeholder="Add any additional context or details..."
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--gray-200)',
                    fontSize: 'var(--text-sm)',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    transition: 'border-color var(--transition-fast)',
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-700)'}
                />
              </div>

              {/* Follow-up Schedule - Only show after hold reason is selected */}
              {formData.holdReason && (
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}>
                    Follow-up Schedule
                  </label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    flexWrap: 'wrap',
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                  }}>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                      Send first reminder in
                    </span>
                    <input
                      id="holdInitialFollowUpDays"
                      type="number"
                      min="1"
                      max="365"
                      value={formData.holdInitialFollowUpDays}
                      onChange={(e) => handleInputChange("holdInitialFollowUpDays", parseInt(e.target.value) || 7)}
                      style={{
                        width: '80px',
                        padding: 'var(--space-2)',
                        backgroundColor: 'var(--dark-bg)',
                        border: '1px solid var(--gray-600)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'inherit',
                        textAlign: 'center',
                        transition: 'border-color var(--transition-fast)',
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-600)'}
                    />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                      days, then every
                    </span>
                    <input
                      id="holdRecurringFollowUpDays"
                      type="number"
                      min="1"
                      max="365"
                      value={formData.holdRecurringFollowUpDays}
                      onChange={(e) => handleInputChange("holdRecurringFollowUpDays", parseInt(e.target.value) || 7)}
                      style={{
                        width: '80px',
                        padding: 'var(--space-2)',
                        backgroundColor: 'var(--dark-bg)',
                        border: '1px solid var(--gray-600)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'inherit',
                        textAlign: 'center',
                        transition: 'border-color var(--transition-fast)',
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-600)'}
                    />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                      days after
                    </span>
                  </div>
                </div>
              )}

              {/* Related Discrepancies - Only show after hold reason is selected */}
              {formData.holdReason && projectDiscrepancies.length > 0 && (
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}>
                    Related Discrepancies
                  </label>
                  <MultiSelectDropdown
                    options={projectDiscrepancies.map(d => `${d.discrepancyType} - ${d.discrepancyStatus}`)}
                    selected={(formData.holdDiscrepancyIds || [])
                      .map((id) => {
                        const d = projectDiscrepancies.find(disc => disc.id === id);
                        return d ? `${d.discrepancyType} - ${d.discrepancyStatus}` : "";
                      })
                      .filter(Boolean)}
                    onChange={(selectedNames) => {
                      const selectedIds = selectedNames
                        .map((name) => {
                          const d = projectDiscrepancies.find(disc => `${disc.discrepancyType} - ${disc.discrepancyStatus}` === name);
                          return d ? d.id : null;
                        })
                        .filter(Boolean);
                      handleInputChange("holdDiscrepancyIds", selectedIds);
                    }}
                    placeholder={loadingDiscrepancies ? "Loading discrepancies..." : "Select related discrepancies (optional)..."}
                    disabled={loadingDiscrepancies}
                  />
                </div>
              )}

              {/* Send Email Toggle - Only show after hold reason is selected */}
              {formData.holdReason && (
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}>
                    Send Hold Notification Email
                  </label>
                  <div style={{
                    display: 'inline-flex',
                    padding: '2px',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    width: 'fit-content',
                  }}>
                    <button
                      type="button"
                      onClick={() => handleInputChange("holdSendEmailEnabled", true)}
                      style={{
                        padding: '6px 20px',
                        border: 'none',
                        borderRadius: 'calc(var(--radius-md) - 2px)',
                        backgroundColor: formData.holdSendEmailEnabled ? 'var(--cta-blue)' : 'transparent',
                        color: formData.holdSendEmailEnabled ? 'white' : 'var(--gray-400)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange("holdSendEmailEnabled", false)}
                      style={{
                        padding: '6px 20px',
                        border: 'none',
                        borderRadius: 'calc(var(--radius-md) - 2px)',
                        backgroundColor: !formData.holdSendEmailEnabled ? 'var(--cta-blue)' : 'transparent',
                        color: !formData.holdSendEmailEnabled ? 'white' : 'var(--gray-400)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Email Recipients and Composition - Only show if email enabled */}
          {formData.holdSendEmailEnabled && formData.holdReason && (
            <>
              {/* Email Recipients Section */}
              <div className="export-form-section">
                <h3>Email Recipients</h3>
                <div className="export-recipients-section">
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-400)',
                    margin: '0 0 var(--space-4) 0'
                  }}>
                    Select contacts for hold notification email
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                    {/* Left: To and CC Fields Stacked */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                      {/* Email To Field */}
                      <div className="export-email-field">
                        <label className="export-email-field-label">
                          To: <span className="required">*</span>
                        </label>
                        <div className="export-email-field-input">
                          <MultiSelectDropdown
                            options={clientContacts.map(c => c.name + (c.email ? ` (${c.email})` : ""))}
                            selected={(formData.holdEmailTo || [])
                              .map((id) => {
                                const c = clientContacts.find(contact => contact.id === id);
                                return c ? c.name + (c.email ? ` (${c.email})` : "") : "";
                              })
                              .filter(Boolean)}
                            onChange={(selectedNames) => {
                              const selectedIds = selectedNames
                                .map((name) => {
                                  const c = clientContacts.find(contact => contact.name + (contact.email ? ` (${contact.email})` : "") === name);
                                  return c ? c.id : null;
                                })
                                .filter(Boolean);
                              handleInputChange("holdEmailTo", selectedIds);
                            }}
                            placeholder="Select email recipients..."
                            disabled={loadingContacts}
                          />
                        </div>
                      </div>

                      {/* Email CC Field */}
                      <div className="export-email-field">
                        <label className="export-email-field-label">CC:</label>
                        <div className="export-email-field-input">
                          <MultiSelectDropdown
                            options={clientContacts
                              .filter(c => !formData.holdEmailTo.includes(c.id))
                              .map(c => c.name + (c.email ? ` (${c.email})` : ""))}
                            selected={(formData.holdEmailCc || [])
                              .map((id) => {
                                const c = clientContacts.find(contact => contact.id === id);
                                return c ? c.name + (c.email ? ` (${c.email})` : "") : "";
                              })
                              .filter(Boolean)}
                            onChange={(selectedNames) => {
                              const selectedIds = selectedNames
                                .map((name) => {
                                  const c = clientContacts.find(contact => contact.name + (contact.email ? ` (${contact.email})` : "") === name);
                                  return c ? c.id : null;
                                })
                                .filter(Boolean);
                              handleInputChange("holdEmailCc", selectedIds);
                            }}
                            placeholder="Add CC recipients (optional)..."
                            disabled={loadingContacts}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Right: Empty space for symmetry (or add New Contact button if needed) */}
                    <div></div>
                  </div>
                </div>
              </div>

              {/* Email Content Section */}
              <div className="export-form-section">
                <h3 style={{ marginBottom: 'var(--space-4)' }}>Email Content</h3>

                {/* Subject Field */}
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <label htmlFor="emailSubject" style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    id="emailSubject"
                    value={formData.holdEmailSubject}
                    onChange={(e) => handleInputChange("holdEmailSubject", e.target.value)}
                    placeholder="Email subject..."
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'inherit',
                      transition: 'border-color var(--transition-fast)',
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-700)'}
                  />
                </div>

                {/* Two-column layout: Custom Message + Preview */}
                <div className="export-email-container">
                  {/* Left Side - Email Editor with Custom Message */}
                  <div className="export-email-editor">
                    <div className="export-email-editor-header">
                      <h4>Custom Message (Optional)</h4>
                      <small>Add additional notes to the auto-generated email</small>
                    </div>
                    <textarea
                      id="customMessage"
                      name="customMessage"
                      value={formData.holdCustomMessage}
                      onChange={(e) => handleInputChange("holdCustomMessage", e.target.value)}
                      placeholder="Add any custom notes here (optional)..."
                      className="export-email-body"
                    />
                  </div>

                  {/* Right Side - HTML Preview */}
                  <div className="export-email-preview">
                    <div className="export-email-preview-header">
                      <h4>Email Preview</h4>
                      <small>How the email will appear</small>
                    </div>
                    <div className="export-email-preview-content">
                      <iframe
                        srcDoc={formData.holdEmailHtml || '<div style="padding: 20px; text-align: center; color: #666;">Email preview will appear here</div>'}
                        title="Email Preview"
                        className="export-email-preview-iframe"
                        sandbox="allow-same-origin allow-scripts"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'var(--space-3)', width: '100%' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleSubmit}
              disabled={saving || !formData.holdReason}
            >
              {saving
                ? "Processing..."
                : formData.holdSendEmailEnabled
                  ? "Put on Hold & Send Email"
                  : "Put Project on Hold"}
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default HoldProjectModal;
